import { useFormatter } from '@api/useFormatter'
import { scheduleColumnDefs } from '@components/OrderDispatch/OptimizerWizard/columnDefs/scheduleColumnDefs'
import { BBDTag, GraviGrid, Horizontal, Texto, useThemeContext, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { AmPmLabel } from '@/components/DriverManagement/Manage/GridColumns/AmPmLabel'
import { formatShiftDate } from '../../util'
import { ICellRendererParams } from 'ag-grid-community'

export function ScheduleGrid({ data, isFetching }) {
  const gridAPIRef = useRef(null)
  const columnApiRef = useRef(null)

  useEffect(() => {
    columnApiRef?.current && columnApiRef.current.setRowGroupColumns(['driver_name', 'shift_start'])
  }, [])
  const getRowId = useCallback((row) => row.data?.trips?.[0]?.order_id ?? row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      groupDisplayType: 'groupRows',
      groupRowRendererParams: {
        innerRenderer: DriverGrouping,
        data,
      },
      groupDefaultExpanded: -1,
      getRowId,
    }),
    []
  )
  const columnDefs = useMemo(() => scheduleColumnDefs(), [])

  return (
    <GraviGrid
      columnApiRef={columnApiRef}
      loading={isFetching}
      rowData={data?.rows || []}
      externalRef={gridAPIRef}
      storageKey='optimizer-results-shift-impact'
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
    />
  )
}

export function DriverGrouping(params: ICellRendererParams) {
  const { formatDistance } = useFormatter()
  const formattedUnit = formatDistance(null, true)?.toUpperCase()
  const { isDarkTheme } = useThemeContext()
  const driver = useMemo(
    () => (params?.node?.parent?.field === 'driver_name' ? params?.node?.parent?.key : ''),
    [params?.node?.parent?.field]
  )
  const data = useMemo(() => {
    if (!driver) return null

    return (
      params?.data?.rows?.filter((row) => formatShiftDate(row) === params.value && row.driver_name === driver)?.[0] ||
      []
    )
  }, [params?.data?.rows, driver, params.value])

  const trips = useMemo(() => {
    if (!driver) return null
    const allRows = params?.data?.rows?.filter(
      (row) => formatShiftDate(row) === params.value && row.driver_name === driver
    )
    const tripList = new Array(10).fill(false)
    allRows?.forEach((row) => (tripList[row.trip_index] = true))
    return tripList
  }, [params?.data?.rows, driver, params.value])

  if (params.node.field !== 'shift_start' || !data) return <>{params?.value}</>

  const { loaded_miles, total_miles, loaded_percentage } = data?.driver_metrics ?? {}
  const { driver_utilization, hours_assigned, hours_available, shift_key } = data ?? {}

  return (
    <Horizontal verticalCenter className='full-height-width' style={{ minWidth: '525px' }}>
      <Vertical className='mr-4' flex={1.5}>
        <Horizontal gap={6} verticalCenter>
          <Texto className='text-truncate'>{params.value}</Texto>
          <AmPmLabel value={shift_key?.includes('.am') ? 'am' : 'pm'} />
        </Horizontal>
      </Vertical>

      <Horizontal flex={1.5} verticalCenter horizontalCenter className='mr-4'>
        {trips?.map((trip, index) => {
          return (
            <Vertical
              style={{
                backgroundColor: trip
                  ? isDarkTheme
                    ? 'var(--theme-color-2-dim)'
                    : 'var(--theme-color-2)'
                  : 'var(--gray-200)',
                height: '8px',
                minWidth: '5px',
                borderLeft: index === 0 ? 'none' : '1px solid var(--gray-400)',
              }}
              key={`${data.id}-trip-${index}`}
              flex={1}
            />
          )
        })}
      </Horizontal>

      <Vertical className='mr-4' flex={1}>
        <Texto className='text-truncate'>
          {formattedUnit}: {loaded_miles} | {total_miles} ({Math.round(loaded_percentage)}%)
        </Texto>
      </Vertical>
      <Vertical flex={1} className='mr-1'>
        <BBDTag error={driver_utilization >= 1} theme2={driver_utilization >= 0.8 && driver_utilization < 1}>
          {hours_assigned}/{hours_available}
        </BBDTag>
      </Vertical>
    </Horizontal>
  )
}
